import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Person as PersonIcon } from '@mui/icons-material';
import {
  getAgentUsers,
  assignUserToAgent,
  removeUserFromAgent,
  updateUserRole,
  getUserAgents,
  CreateAgentUserRQ,
  UpdateUserRoleRQ
} from '../../utils/api';
import { userApi } from '../../utils/userApi';

export interface AgentUsersDialogProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  agentName: string;
}

interface AgentUser {
  id: string;
  agentId: string;
  userId: string;
  role: 'Owner' | 'Manager' | 'User' | 'Viewer';
  assignedAt: string;
  assignedBy: string;
  isActive: boolean;
  userName?: string;
  userUsername?: string;
  userPhone?: string;
}

interface User {
  id: string;
  name: string;
  username: string;
  phone: string;
  role: string;
  managerUserId?: string | null;
  active: boolean;
  shouldResetPassword: boolean;
}

const AgentUsersDialog: React.FC<AgentUsersDialogProps> = ({ open, onClose, agentId, agentName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [agentUsers, setAgentUsers] = useState<AgentUser[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('User');
  const [error, setError] = useState<string | null>(null);
  const [userAgentAssignments, setUserAgentAssignments] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (open && agentId) {
      loadAgentUsers();
      loadAvailableUsers();
    }
  }, [open, agentId]);

  const loadAgentUsers = async () => {
    setLoading(true);
    try {
      const response = await getAgentUsers(agentId);
      setAgentUsers(response.agentUsers || []);
    } catch (err) {
      setError('Falha ao carregar usuários do agente');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const users = await userApi.list();
      const activeUsers = users.filter(user => user.active);
      setAvailableUsers(activeUsers);

      // Check which agents users are already assigned to (for informational purposes)
      const assignments = new Map<string, string>();
      for (const user of users) {
        try {
          const userAgents = await getUserAgents(user.id);
          const otherAgents = userAgents.filter(au => au.agentId !== agentId);

          if (otherAgents.length > 0) {
            const agentNames = otherAgents.map(au => au.agentName || 'Unknown Agent').join(', ');
            assignments.set(user.id, agentNames);
          }
        } catch (err) {
          // Ignore errors for individual user checks
        }
      }
      setUserAgentAssignments(assignments);
    } catch (err) {
      setError('Falha ao carregar usuários disponíveis');
    }
  };

  const handleAssignUser = async () => {
    if (!selectedUserId || !selectedRole) return;

    setIsCreating(true);
    try {
      const result = await assignUserToAgent({
        agentId,
        userId: selectedUserId,
        role: selectedRole as any,
        isActive: true
      });

      // Reload the agent users to get the updated list
      await loadAgentUsers();

      setShowCreateForm(false);
      setSelectedUserId('');
      setSelectedRole('User');
      setError(null); // Clear any previous errors
    } catch (err: any) {
      setError(err.message || 'Falha ao atribuir usuário ao agente');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRemoveUser = async (agentUserId: string) => {
    try {
      await removeUserFromAgent(agentUserId);
      // Reload the agent users to get the updated list
      await loadAgentUsers();
    } catch (err) {
      setError('Falha ao remover usuário do agente');
    }
  };

  const handleUpdateRole = async (agentUserId: string, newRole: string) => {
    try {
      await updateUserRole(agentUserId, { role: newRole as any });
      // Reload the agent users to get the updated list
      await loadAgentUsers();
    } catch (err) {
      setError('Falha ao atualizar função do usuário');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Owner': return 'error';
      case 'Manager': return 'warning';
      case 'User': return 'primary';
      case 'Viewer': return 'default';
      default: return 'default';
    }
  };

  const translateRole = (role: string) => {
    switch (role) {
      case 'Owner': return 'Proprietário';
      case 'Manager': return 'Gerente';
      case 'User': return 'Usuário';
      case 'Viewer': return 'Visualizador';
      default: return role;
    }
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    let displayNumber = cleaned;
    if (displayNumber.startsWith('55') && displayNumber.length >= 12) {
      displayNumber = displayNumber.substring(2);
    }
    if (displayNumber.length >= 10) {
      return `+55 (${displayNumber.substring(0, 2)}) ${displayNumber.substring(2, 7)}-${displayNumber.substring(7)}`;
    }
    return phoneNumber;
  };

  const availableUsersForAssignment = availableUsers.filter(user =>
    !agentUsers.some(au => au.userId === user.id && au.isActive)
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1}
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 0.5, sm: 1 }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon />
            <Typography 
              variant="h6"
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: '200px', sm: 'none' }
              }}
            >
              Gerenciar Usuários
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '250px', sm: 'none' }
            }}
          >
            {agentName}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            sx={{
              color: theme.palette.mode === 'dark' ? '#000' : undefined,
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
            startIcon={<AddIcon />}
            onClick={() => setShowCreateForm(true)}
            disabled={availableUsersForAssignment.length === 0}
          >
            Atribuir Usuário ao Agente
          </Button>
          {availableUsersForAssignment.length === 0 && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                ml: { xs: 0, sm: 1 },
                mt: { xs: 1, sm: 0 },
                display: 'block',
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              Todos os usuários disponíveis já foram atribuídos a este agente
            </Typography>
          )}
        </Box>

        {showCreateForm && (
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
            <Typography 
              variant="subtitle1" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              Atribuir Novo Usuário
            </Typography>
            <Box 
              display="flex" 
              gap={2} 
              alignItems="center"
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.5, sm: 2 }
              }}
            >
              <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <InputLabel>Selecionar Usuário</InputLabel>
                <Select
                  value={selectedUserId}
                  onChange={(e: SelectChangeEvent) => setSelectedUserId(e.target.value)}
                  label="Selecionar Usuário"
                >
                  {availableUsersForAssignment.map((user) => {
                    const assignedToAgent = userAgentAssignments.get(user.id);
                    return (
                      <MenuItem key={user.id} value={user.id}>
                        <Box>
                          <Typography variant="body2">
                            {user.name} ({user.username})
                          </Typography>
                          {assignedToAgent && (
                            <Typography variant="caption" color="info.main">
                              Também atribuído a: {assignedToAgent}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {/*               
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Função</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={(e: SelectChangeEvent) => setSelectedRole(e.target.value)}
                  label="Função"
                >
                  <MenuItem value="Owner">Proprietário</MenuItem>
                  <MenuItem value="Manager">Gerente</MenuItem>
                  <MenuItem value="User">Usuário</MenuItem>
                  <MenuItem value="Viewer">Visualizador</MenuItem>
                </Select>
              </FormControl> */}

              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 1,
                  width: { xs: '100%', sm: 'auto' },
                  flexDirection: { xs: 'column', sm: 'row' }
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleAssignUser}
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#000' : undefined,
                    width: { xs: '100%', sm: 'auto' },
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                  disabled={!selectedUserId || isCreating}
                >
                  {isCreating ? <CircularProgress size={20} /> : 'Atribuir'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCreateForm(false);
                    setSelectedUserId('');
                    setSelectedRole('User');
                  }}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  Cancelar
                </Button>
              </Box>

              {selectedUserId && userAgentAssignments.has(selectedUserId) && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: { xs: 1, sm: 1 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  Este usuário está atualmente atribuído a outro(s) agente(s): {userAgentAssignments.get(selectedUserId)}. Ele será adicionado a este agente também.
                </Alert>
              )}
            </Box>
          </Paper>
        )}

        <TableContainer 
          component={Paper}
          sx={{
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark' ? '#686868' : '#c1c1c1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#888888' : '#a8a8a8',
            },
          }}
        >
          <Table sx={{ minWidth: { xs: '600px', sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Usuário</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Nome de Usuário</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Telefone</TableCell>
                {/* <TableCell>Função</TableCell> */}
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Atribuído Em</TableCell>
                {/* <TableCell>Atribuído Por</TableCell> */}
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : agentUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      Nenhum usuário atribuído a este agente
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                agentUsers.map((agentUser) => (
                  <TableRow key={agentUser.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" />
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {agentUser.userName || 'Usuário Desconhecido'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {agentUser.userUsername || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {agentUser.userPhone ? formatPhoneNumber(agentUser.userPhone) : 'N/A'}
                    </TableCell>
                    {/* <TableCell>
                      <Box display="flex" gap={1}>
                        <Chip
                          label={translateRole(agentUser.role)}
                          color={getRoleColor(agentUser.role) as any}
                          size="small"
                        />
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={agentUser.role}
                            onChange={(e) => handleUpdateRole(agentUser.id, e.target.value)}
                            variant="standard"
                          >
                            <MenuItem value="Owner">Proprietário</MenuItem>
                            <MenuItem value="Manager">Gerente</MenuItem>
                            <MenuItem value="User">Usuário</MenuItem>
                            <MenuItem value="Viewer">Visualizador</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </TableCell> */}
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {(() => {
                        const date = new Date(agentUser.assignedAt);
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                      })()}
                    </TableCell>
                    {/* <TableCell>{agentUser.assignedBy}</TableCell> */}
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveUser(agentUser.id)}
                        color="error"
                        sx={{ 
                          padding: { xs: '4px', sm: '8px' },
                          '& .MuiSvgIcon-root': {
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 1, sm: 2 } }}>
        <Button 
          onClick={onClose}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentUsersDialog;
